import React, { useEffect } from 'react';
import { useQuiz } from '../contexts/QuizContext';
import './QuizResult.css';
import { Link } from 'react-router-dom';

const QuizResult = () => {
  const { tipoDeBarriga, resetQuiz } = useQuiz();
  
  // Dispara o evento de visualização do resultado para o Facebook Pixel
  useEffect(() => {
    if (tipoDeBarriga && window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_type: 'quiz_result',
        content_name: `Resultado: ${tipoDeBarriga.tipo}`
      });
    }
  }, [tipoDeBarriga]);
  
  if (!tipoDeBarriga) {
    return null;
  }
  
  const { tipo, descricao, naoDeve, deve } = tipoDeBarriga;
  
  // Função para personalizar o texto CTA baseado no tipo de barriga
  const getCtaText = () => {
    switch (tipo) {
      case 'Pochete Inflamatória':
        return "QUERO ELIMINAR MINHA POCHETE AGORA!";
      case 'Barriga Metabólica':
        return "QUERO EQUILIBRAR MEU METABOLISMO AGORA!";
      case 'Barriga Avental':
        return "QUERO ELIMINAR MINHA FLACIDEZ AGORA!";
      case 'Pochete Sem Tentativas':
        return "QUERO RESULTADOS RÁPIDOS E DURADOUROS!";
      case 'Barriga Metabólica Inicial':
        return "QUERO ACELERAR MEU METABOLISMO AGORA!";
      case 'Avental Leve':
        return "QUERO TONIFICAR MINHA BARRIGA AGORA!";
      default:
        return "QUERO ELIMINAR MINHA BARRIGA AGORA!";
    }
  };
  
  // Descrição da dor personalizada para cada tipo de barriga
  const getDescricaoDor = () => {
    switch (tipo) {
      case 'Pochete Inflamatória':
        return "Essa gordurinha abaixo do umbigo incomoda bastante, não é? A sensação de nunca conseguir eliminá-la mesmo tentando dietas, exercícios ou cortando carboidratos já cansou muita gente.";
      case 'Barriga Metabólica':
        return "Essa barriga arredondada e dura é frustrante, não é? Mesmo quando você perde peso, ela parece ser a última a ir embora, resistindo a dietas e exercícios convencionais.";
      case 'Barriga Avental':
        return "Essa flacidez na barriga é desconfortável e afeta sua autoestima, não é? A sensação de que nada resolve, nem dietas nem os exercícios tradicionais, pode ser bastante frustrante.";
      case 'Pochete Sem Tentativas':
        return "Essa gordurinha abaixo do umbigo já começou a incomodar, não é? Você quer evitar que ela aumente e quer resultados rápidos antes que se torne mais difícil de eliminar.";
      case 'Barriga Metabólica Inicial':
        return "Essa barriga arredondada que está começando a aparecer te preocupa, não é? É o momento perfeito para resolver antes que se torne um problema mais difícil.";
      case 'Avental Leve':
        return "Essa flacidez inicial na região da barriga já te incomoda, não é? Você percebe que precisa agir agora antes que a situação piore e fique mais difícil de reverter.";
      default:
        return "Essa gordura localizada na barriga incomoda bastante, não é? A sensação de nunca conseguir eliminá-la mesmo tentando diferentes métodos já frustrou muita gente.";
    }
  };
  
  // Quebra de paradigma personalizada para cada tipo
  const getQuebraParadigma = () => {
    switch (tipo) {
      case 'Pochete Inflamatória':
        return "A verdade é que as dietas restritivas, low-carb, ou o famoso 'comer de tudo um pouco' só atrapalham, porque focam em cortar calorias e não resolvem a causa real: a inflamação.";
      case 'Barriga Metabólica':
        return "A verdade é que exercícios intensos, dietas da moda e até o jejum intermitente podem piorar seu caso, pois aumentam o cortisol e prejudicam ainda mais seu metabolismo já desregulado.";
      case 'Barriga Avental':
        return "A verdade é que os abdominais tradicionais, cardio excessivo e dietas de restrição severa não vão resolver o problema da flacidez, podendo até piorar a aparência da sua barriga.";
      case 'Pochete Sem Tentativas':
        return "A verdade é que iniciar com métodos muito restritivos ou exercícios intensos pode prejudicar seu metabolismo a longo prazo e criar um ciclo de perda e recuperação de peso.";
      case 'Barriga Metabólica Inicial':
        return "A verdade é que seguir conselhos genéricos como 'coma menos e se exercite mais' pode não funcionar para seu tipo de corpo e metabolismo, levando a ciclos frustrantes de resultados temporários.";
      case 'Avental Leve':
        return "A verdade é que os exercícios abdominais tradicionais e dietas comuns não vão tonificar sua barriga, pois não abordam a perda de tônus muscular e a produção de colágeno.";
      default:
        return "A verdade é que os métodos convencionais de perda de peso não funcionam para eliminar sua barriga de forma específica, pois não consideram as causas reais e únicas do seu tipo de gordura.";
    }
  };
  
  // Solução única personalizada para cada tipo
  const getSolucaoUnica = () => {
    switch (tipo) {
      case 'Pochete Inflamatória':
        return "Para eliminar de verdade essa gordura, você precisa ativar o seu sistema natural de queima através de Alimentação Inteligente e Anti-inflamatória, focando em alimentos estratégicos que combatem a inflamação e reequilibram seus hormônios.";
      case 'Barriga Metabólica':
        return "Para eliminar essa barriga, você precisa reequilibrar naturalmente seus hormônios através de alimentos específicos e técnicas de redução de estresse que normalizam o cortisol e aceleram seu metabolismo naturalmente.";
      case 'Barriga Avental':
        return "Para recuperar a firmeza abdominal, você precisa de um protocolo específico que combine alimentação rica em colágeno e proteínas com exercícios estratégicos que restauram a força e elasticidade da musculatura profunda.";
      case 'Pochete Sem Tentativas':
        return "Para eliminar essa pochete rapidamente, você precisa de um protocolo inteligente que acelere seu metabolismo de forma saudável, aproveitando que seu corpo ainda está responsivo e não passou por dietas prejudiciais.";
      case 'Barriga Metabólica Inicial':
        return "Para reverter essa barriga inicial, você precisa regular seu metabolismo com alimentos estratégicos consumidos nos horários certos, evitando que o problema se agrave e se torne mais difícil de resolver no futuro.";
      case 'Avental Leve':
        return "Para tonificar sua barriga, você precisa estimular a produção natural de colágeno e ativar a musculatura abdominal profunda através de uma alimentação construtora e exercícios específicos de alta eficácia.";
      default:
        return "Para eliminar de verdade sua barriga, você precisa de um protocolo personalizado que ataque as causas específicas do seu tipo de gordura, reequilibrando seu metabolismo e ativando a queima localizada.";
    }
  };
  
  // Depoimentos reais
  const depoimentos = [
    {
      nome: "Mariane Souza, 42 anos",
      texto: "Depois de tentar de tudo, finalmente encontrei o que funcionou! Em 21 dias, minha pochete diminuiu visivelmente e me sinto muito mais disposta!",
      imagem: "/images/depoimentos/mariane_provas.png"
    },
    {
      nome: "Luciana Alves, 38 anos",
      texto: "Incrível como algo tão simples funciona tão bem! Minha barriga estava resistente mesmo com academia 3x por semana, mas em 3 semanas com o protocolo ela já mudou drasticamente!",
      imagem: "/images/depoimentos/luciana_alves.jpeg"
    },
    {
      nome: "Maria Luísa Silveira, 45 anos",
      texto: "Eu já não acreditava mais que conseguiria eliminar aquela flacidez na barriga depois de 2 gestações. O protocolo me surpreendeu já nas primeiras semanas!",
      imagem: "/images/depoimentos/maria_luisa_silveira.jpeg"
    },
    {
      nome: "Maria Isabel, 51 anos",
      texto: "Mesmo com a idade mais avançada, consegui resultados impressionantes em apenas 21 dias. Minha barriga está mais firme e a inflamação reduziu completamente.",
      imagem: "/images/depoimentos/maria_isabel_vinhando.jpeg"
    },
    {
      nome: "Ana Panao, 47 anos",
      texto: "Já tinha aceitado que aquela gordurinha na barriga era parte da minha vida após os 45, mas esse protocolo provou que eu estava errada! Em 3 semanas já notei diferença!",
      imagem: "/images/depoimentos/ana-panao.jpg"
    },
    {
      nome: "Tatiane Cabral, 39 anos",
      texto: "Não precisei fazer dietas malucas nem passar fome. Alimentação inteligente e exercícios estratégicos realmente funcionam! Minha barriga está irreconhecível!",
      imagem: "/images/depoimentos/tatiane-cabral.jpg"
    }
  ];
  
  // Selecionar depoimentos específicos com base no tipo de barriga
  const getDepoimentosPorTipo = () => {
    switch (tipo) {
      case 'Pochete Inflamatória':
        return [depoimentos[0], depoimentos[4]];
      case 'Barriga Metabólica':
        return [depoimentos[1], depoimentos[5]];
      case 'Barriga Avental':
        return [depoimentos[2], depoimentos[3]];
      case 'Pochete Sem Tentativas':
        return [depoimentos[0], depoimentos[5]];
      case 'Barriga Metabólica Inicial':
        return [depoimentos[1], depoimentos[4]];
      case 'Avental Leve':
        return [depoimentos[2], depoimentos[5]];
      default:
        return [depoimentos[0], depoimentos[3]];
    }
  };
  
  const depoimentosFiltrados = getDepoimentosPorTipo();
  
  return (
    <div className="quiz-result">
      <div className="result-container">
        {/* Bloco 1 - Headline de resultado personalizado */}
        <div className="result-block block-headline">
          <h2 className="result-title">Seu tipo de barriga é:</h2>
          <h3 className="result-type">{tipo}!</h3>
        </div>
        
        {/* Bloco 2 - Descrição da Dor */}
        <div className="result-block block-dor">
          <p>{getDescricaoDor()}</p>
        </div>
        
        {/* Bloco 3 - Quebra de Paradigma */}
        <div className="result-block block-paradigma">
          <p>{getQuebraParadigma()}</p>
        </div>
        
        {/* Bloco 4 - Solução Única */}
        <div className="result-block block-solucao">
          <p>{getSolucaoUnica()}</p>
        </div>
        
        {/* Bloco 5 - Prova de Resultado */}
        <div className="result-block block-prova">
          <h4>Resultados Reais em Apenas 21 Dias</h4>
          <div className="testimonials">
            {depoimentosFiltrados.map((depoimento, index) => (
              <div className="testimonial-container" key={index}>
                <div className="testimonial-images">
                  <div className="before-after-container">
                    <img 
                      src={depoimento.imagem} 
                      alt={`Antes e Depois de ${depoimento.nome}`} 
                      className="before-after-image real-image"
                    />
                    <span>{depoimento.nome}</span>
                  </div>
                </div>
                <p className="testimonial-text">
                  "{depoimento.texto}"
                </p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bloco 6 - Detalhes do Produto */}
        <div className="result-block block-produto">
          <h4>O Que Você Vai Receber:</h4>
          <ul className="beneficios-lista">
            <li><span className="check">✓</span> Guia Digital Completo e Personalizado para você realizar durante 21 dias</li>
            <li><span className="check">✓</span> Receitas anti-inflamatórias rápidas e com ingredientes baratos</li>
            <li><span className="check">✓</span> Shots e Chás metabólicos que aceleram seus resultados</li>
            <li><span className="check">✓</span> Crononutrição: hora certa de comer cada alimento</li>
            <li><span className="check">✓</span> Ativador de Fase Metabólica: Ativar a queima localizada</li>
          </ul>
        </div>
        
        {/* Bloco 7 - Oferta Irresistível */}
        <div className="result-block block-oferta">
          <div className="price-container">
            <p className="price-comparison">Investimento menor que uma pizza no mês!</p>
            <div className="price-tags">
              <span className="old-price">R$97,00</span>
              <span className="current-price">R$19,90</span>
            </div>
            <p className="price-installment">ou 4x de R$5,42</p>
          </div>
        </div>
        
        {/* Bloco 8 - CTA */}
        <div className="result-block block-cta">
          <a href="https://pay.kiwify.com.br/DzyHrxg" className="cta-button">
            {getCtaText()}
          </a>
          <p className="secure-payment">Pagamento 100% Seguro</p>
        </div>
        
        {/* Bloco 9 - Garantia */}
        <div className="result-block block-garantia">
          <div className="garantia-container">
            <div className="garantia-seal">
              <div className="garantia-circle">7</div>
              <div className="garantia-text-seal">DIAS</div>
            </div>
            <div className="garantia-text">
              <h4>Garantia de Satisfação</h4>
              <p>Se você não começar a ver resultados nos primeiros 7 dias, devolvemos 100% do seu dinheiro. Sem perguntas, sem burocracia!</p>
            </div>
          </div>
        </div>
        
        {/* Bloco 10 - FAQ */}
        <div className="result-block block-faq">
          <h4>Perguntas Frequentes</h4>
          <div className="faq-item">
            <h5>Esse protocolo funciona para qualquer idade?</h5>
            <p>Sim! O protocolo foi desenvolvido para mulheres de todas as idades, com resultados comprovados dos 20 aos 65 anos.</p>
          </div>
          <div className="faq-item">
            <h5>Os resultados do protocolo são rápidos mesmo?</h5>
            <p>Sim! A maioria das mulheres começa a notar mudanças visíveis já na primeira semana, com resultados significativos ao completar os 21 dias. Nosso protocolo foi desenvolvido para promover uma transformação rápida e sustentável.</p>
          </div>
          <div className="faq-item">
            <h5>Já passei dos 40 e estou na menopausa, consigo secar com o programa?</h5>
            <p>Absolutamente! O Protocolo 21 Dias foi especialmente formulado considerando as mudanças hormonais que ocorrem na menopausa. Muitas mulheres acima dos 40, 50 e até 60 anos tiveram resultados impressionantes, pois o método trabalha especificamente com o reequilíbrio hormonal.</p>
          </div>
          <div className="faq-item">
            <h5>Tenho problemas de saúde e/ou hormonais, o programa funciona para mim?</h5>
            <p>Sim! O protocolo foi desenvolvido para trabalhar com diferentes condições de saúde. Os alimentos e técnicas recomendados ajudam a regular o metabolismo e equilibrar os hormônios naturalmente, sendo ideais para quem tem problemas como hipotireoidismo, resistência à insulina, SOP ou desequilíbrios hormonais.</p>
          </div>
          <div className="faq-item">
            <h5>Vou precisar comprar alimentos caros ou especiais?</h5>
            <p>Não! Usamos apenas alimentos comuns que você encontra em qualquer supermercado por preços acessíveis.</p>
          </div>
          <div className="faq-item">
            <h5>Funciona mesmo para quem já tentou de tudo?</h5>
            <p>Especialmente para quem já tentou outros métodos! O Protocolo 21 Dias ataca a causa raiz do seu tipo específico de barriga.</p>
          </div>
        </div>
        
        {/* Refazer o Teste */}
        <button onClick={resetQuiz} className="reset-button">
          Refazer o Teste
        </button>
      </div>
    </div>
  );
};

export default QuizResult; 